import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Answer, Conditions, AssessmentQuestion } from '../types/assessmentQuestion';
import { resolveQuestionChoices, type ActiveQuestion } from '../utils/resolveQuestionChoices';

export type { ActiveQuestion, ResolvedChoice } from '../utils/resolveQuestionChoices';

// Ported from stylos's useQuestions: walks a question group one question at a
// time, applying `conditions` (stop / next-only / jump) to decide the next
// question, and calls `onFinish` once the group is answered.
export function useAssessmentQuestions(
  questions: AssessmentQuestion[],
  conditions: Conditions = {}
) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [activeQuestion, setActiveQuestion] = useState<ActiveQuestion>();

  const parsedQuestions = useMemo<ActiveQuestion[]>(
    () => resolveQuestionChoices(questions),
    [questions]
  );

  const setNextActiveQuestion = useCallback(
    (answer: Answer, onFinish: () => void) => {
      if (questionIndex === parsedQuestions.length - 1) {
        return onFinish();
      }

      const condition = conditions[answer.questionKey];
      const choiceOrder = (parsedQuestions[questionIndex]?.choices ?? []).find(
        ({ value }) => value === answer.questionValue
      )?.order;

      if (condition) {
        if (condition.stopIf === choiceOrder) return onFinish();
        if (typeof condition.nextIf === 'number' && condition.nextIf !== choiceOrder) {
          return onFinish();
        }
      }

      let nextQuestionIndex = questionIndex + 1;
      if (condition?.jumpToIf && condition.jumpToIf.choice === choiceOrder) {
        const possibleNextIndex = parsedQuestions.findIndex(
          (question) => question.order === condition.jumpToIf?.nextQuestion
        );
        if (possibleNextIndex > nextQuestionIndex) nextQuestionIndex = possibleNextIndex;
      }

      return setQuestionIndex(nextQuestionIndex);
    },
    [conditions, questionIndex, parsedQuestions]
  );

  useEffect(() => {
    setActiveQuestion(parsedQuestions[questionIndex]);
  }, [questionIndex, parsedQuestions]);

  // Exposed so a grouped (`singleScreen`) render can reuse the SAME memoized
  // resolution — keyed on `questions`, so it stays referentially stable across
  // an AssessmentContent re-render that doesn't change `questions` itself
  // (e.g. a keystroke in some field) — rather than recomputing it a second
  // time from scratch on every render.
  return { activeQuestion, setNextActiveQuestion, parsedQuestions };
}
